import { RecruitmentMarketingProvider } from '@/utils/types';

const rootEnv = window.LUCID_SPA_CONFIG;

const API_BASE_PATH = import.meta.env.DEV ? '/api' : rootEnv?.APIURL;
const LAS_API_BASE_PATH = import.meta.env.DEV
  ? '/las-api'
  : rootEnv?.REACT_APP_LAS_API_URL;

export const URLS = {
  getStudyTypes: () => `${API_BASE_PATH}/opportunities/study-types`,
  getCountries: () => `${API_BASE_PATH}/countries`,
  getCurrencies: () => `${API_BASE_PATH}/currencies`,
  getLanguages: () => `${API_BASE_PATH}/languages`,
  getISOLanguages: () => `${API_BASE_PATH}/v2/languages`,
  getCountryDefaults: (countryCode?: string) =>
    `${API_BASE_PATH}/countries/${countryCode}/defaults`,
  getCountryLanguages: () => `${API_BASE_PATH}/country-languages`,
  createPanel: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panels`,
  getAllPanels: () => `${API_BASE_PATH}/companies/panels`,
  getCompanyPanels: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panels`,
  getAllCompanyPanels: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panels/all?order=Asc`,
  getPanel: (panelId?: string) => `${API_BASE_PATH}/panels/${panelId}`,
  getIsPanelProvisioned: (panelId?: string) =>
    `${API_BASE_PATH}/provisioning/panels/${panelId}`,
  updateIsPanelProvisioned: (panelId?: string) =>
    `${API_BASE_PATH}/provisioning/panels/${panelId}`,
  panelSettings: (panelId?: string) =>
    `${API_BASE_PATH}/panels/${panelId}/settings`,
  panelBrand: (panelId?: string) => `${API_BASE_PATH}/panels/${panelId}/brand`,
  panelPaymentSettings: (panelId?: string) =>
    `${API_BASE_PATH}/panels/${panelId}/payout-denomination`,
  panelUrls: (panelId?: string) => `${API_BASE_PATH}/panels/${panelId}/urls`,
  updatePanelStatus: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/status`,
  getPanelPaymentMethods: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/panelpaymentmethods`,
  getPanelPaymentMethodsV2: (panelId: string) =>
    `${API_BASE_PATH}/v2/panels/${panelId}/panelpaymentmethods`,

  getPanelPaymentMethod: (panelPaymentMethodId: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}`,
  getPanelPaymentMethodV2: (panelPaymentMethodId: string) =>
    `${API_BASE_PATH}/v2/panels/panelpaymentmethods/${panelPaymentMethodId}`,
  updatePanelPaymentMethodStatus: (
    panelId?: string,
    paymentMethodId?: string | null
  ) =>
    `${API_BASE_PATH}/panels/${panelId}/panelpaymentmethods/${paymentMethodId}/status`,
  getCompanies: () => `${API_BASE_PATH}/companies`,
  getCompanyById: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}`,
  getTranslations: () => `${API_BASE_PATH}/translations`,
  createTranslation: () => `${API_BASE_PATH}/translations`,
  updateTranslation: (translationId?: string) =>
    `${API_BASE_PATH}/translations/${translationId}`,
  getTranslation: (translationId?: string) =>
    `${API_BASE_PATH}/translations/${translationId}`,
  getTranslationFile: (translationId: string) =>
    `${API_BASE_PATH}/translations/${translationId}/file`,
  startBulkTranslationsUpdateProcess: (bulkBatchId?: string) =>
    `${API_BASE_PATH}/translations/bulk/${bulkBatchId}`,
  createBulkTranslationsUpdateTask: () => `${API_BASE_PATH}/translations/bulk`,
  getLedgerReasons: () =>
    `${API_BASE_PATH}/panelists/ledgers/transaction-reasons`,
  createLedgerEntry: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/ledger-items`,
  getPaymentMethods: () => `${API_BASE_PATH}/payments/paymentmethods`,
  getPaymentMethodsWithTemplates: (queryString: string) =>
    `${API_BASE_PATH}/payments/paymentmethods?${queryString}`,
  getPaymentMethod: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/payments/paymentmethods/${paymentMethodId}`,
  getVoucherCodes: () => `${API_BASE_PATH}/payments/paymentmethods/codes/stats`,
  createPaymentMethod: () => `${API_BASE_PATH}/payments/paymentmethods`,
  createPaymentMethodV2: () => `${API_BASE_PATH}/v2/payments/paymentmethods`,
  updatePaymentMethod: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/payments/paymentmethods/${paymentMethodId}`,
  updatePaymentMethodV2: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/v2/payments/paymentmethods/${paymentMethodId}`,
  updatePanelPaymentMethod: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/v2/panels/panelpaymentmethods/${paymentMethodId}`,
  updatePaymentMethodStatus: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/payments/paymentmethods/${paymentMethodId}/status`,
  getDefaultTemplates: () => `${API_BASE_PATH}/defaulttemplates`,
  paymentMethodUploadCode: (paymentMethodId?: string) =>
    `${API_BASE_PATH}/payments/paymentmethods/${paymentMethodId}/batches`,
  deletePaymentMethodUploadCode: (
    paymentMethodId?: string,
    paymentCodeBatchId?: string
  ) =>
    `${API_BASE_PATH}/payments/paymentmethods/${paymentMethodId}/batches/${paymentCodeBatchId}`,
  getPayoutConfirmationMailTemplatesList: (language_code?: string) =>
    `${API_BASE_PATH}/defaulttemplates/dropdown?mail_type=payout_confirmation&language_code=${language_code}`,
  createPayoutConfirmationMailMethod: () => `${API_BASE_PATH}/defaulttemplates`,
  getPayoutConfirmationMailMethod: (defaultTemplateId?: string) =>
    `${API_BASE_PATH}/defaulttemplates/${defaultTemplateId}`,
  getPanelistsByEmail: () => `${API_BASE_PATH}/panelists`,
  getPanelistsByPanelistId: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}`,
  getLedgerItems: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/ledger-items`,
  getParticipations: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/participations`,
  getPayouts: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/payouts`,
  getPanelistsBanningReasons: () =>
    `${API_BASE_PATH}/panelists/banning-reasons`,
  getPanelistsUnBanningReasons: () =>
    `${API_BASE_PATH}/panelists/unbanning-reasons`,
  updatePanelistBanUnBan: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/ban-status`,
  updatePanelist: (panelistId: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}`,
  getBanStatusHistory: (panelistId?: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/ban-status-history`,
  createBulkBanTask: () => `${API_BASE_PATH}/panelists/bulk-ban`,
  createBulkAdjustBalanceTask: () =>
    `${API_BASE_PATH}/panelists/ledgers/bulk-ledger-items`,
  startBulkBanProcess: (bulkBatchId?: string) =>
    `${API_BASE_PATH}/panelists/bulk-ban/${bulkBatchId}`,
  startBulkBalanceAdjustProcess: (bulkBatchId?: string) =>
    `${API_BASE_PATH}/panelists/ledgers/bulk-ledger-items/${bulkBatchId}`,
  getTrackingTemplate: (singleTemplateId?: string) =>
    `${API_BASE_PATH}/tracking-templates/${singleTemplateId}`,
  getTrackingTemplateIntegrationTypes: () =>
    `${API_BASE_PATH}/tracking-templates/integration-types`,
  updateTrackingTemplate: (singleTemplateId?: string) =>
    `${API_BASE_PATH}/tracking-templates/${singleTemplateId}`,
  createTrackingTemplate: () => `${API_BASE_PATH}/tracking-templates/`,
  getTrackingTemplates: () => `${API_BASE_PATH}/tracking-templates`,
  createMarketingCampaign: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/marketing-campaigns`,
  getMarketingCampaigns: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/marketing-campaigns`,
  getMarketingCampaignProviderTypes: () =>
    `${API_BASE_PATH}/marketing-campaigns/provider-types`,
  getMarketingCampaign: (panelId: string, campaignId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/marketing-campaigns/${campaignId}`,
  updateMarketingCampaign: (panelId: string, campaignId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/marketing-campaigns/${campaignId}`,
  deleteMarketingCampaign: (panelId: string, campaignId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/marketing-campaigns/${campaignId}`,
  generateRecruitmentMarketingAuthUrl: (
    provider: RecruitmentMarketingProvider
  ) => `${API_BASE_PATH}/recruitment-marketing/${provider}/auth-url`,
  createRecruitmentMarketingConnection: (
    provider: RecruitmentMarketingProvider
  ) => `${API_BASE_PATH}/recruitment-marketing/${provider}/connections`,
  getRecruitmentMarketingAccounts: (connectionId: string) =>
    `${API_BASE_PATH}/recruitment-marketing/connections/${connectionId}/accounts`,
  setRecruitmentMarketingPixel: (connectionId: string) =>
    `${API_BASE_PATH}/recruitment-marketing/connections/${connectionId}/accounts`,
  reconnectRecruitmentMarketingConnection: (connectionId: string) =>
    `${API_BASE_PATH}/recruitment-marketing/connections/${connectionId}/reconnect`,
  getTremendousProductsList: (country_code?: string, currency_code?: string) =>
    `${API_BASE_PATH}/tremendous/products?country=${country_code}&currency=${currency_code}`,
  getCompanyMetrics: (companyId: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/metrics-year-to-date`,
  getMarketingCampaignReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/marketing-campaign-data?${queryString}`,
  getCampaignSources: (companyId: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/recruitment-sources`,
  refreshAdminHubSuppliers: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/admin-hub-suppliers/refresh`,
  getAdminHubSuppliers: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/admin-hub-suppliers`,
  getPanelMetrics: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/panel-metrics?${queryString}`,
  getFinancialReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/panel-financial?${queryString}`,
  getLiabilityReportData: (companyId: string) =>
    `${API_BASE_PATH}/v2/reports/companies/${companyId}/liabilities`,
  getProfitabilityReportData: (companyId: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/profitability-branded-panels-details`,
  getRevenueReportData: (companyId: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/revenues`,
  getProfitabilityBrandedPanelsReportData: (companyId: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/profitability-branded-panels`,
  getSurveyReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/panel-surveys?${queryString}`,
  getRetentionReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/panel-retentions?${queryString}`,
  getDemographicsReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/demographics?${queryString}`,
  getOverviewReportData: (companyId: string, queryString: string) =>
    `${API_BASE_PATH}/reports/companies/${companyId}/panel-overview?${queryString}`,
  getPanelProvisioningWorkflow: (panelId: string, marketplace?: string) =>
    `${API_BASE_PATH}/provisioning/panels/${panelId}/workflows${
      marketplace ? `?marketplace=${marketplace}` : ''
    }`,
  getPanelExport: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panel-export-reports`,
  downloadCompanyPanelExport: (companyId: string, requestId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panel-export-reports/${requestId}`,
  createCompanyPanelExport: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/panel-export-reports`,
  panelReferralSettings: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/referral-settings`,
  getPanelistsImports: (panelId?: string) =>
    `${API_BASE_PATH}/panels/${panelId}/panelists-import`,
  createImportPanelistsTask: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/panelists-import`,
  getPanelistsImportErrorFile: (panelId: string, importId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/panelists-import/${importId}/file`,
  processImportPanelists: (panelId: string, importId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/panelists-import/${importId}`,
  checkCExEligibilityByCountryLanguage: (
    country_code: string,
    language_code: string
  ) =>
    `${API_BASE_PATH}/provisioning/eligibility/cint-exchange?country_code=${country_code}&language_code=${language_code}`,
  createImportLeadsTask: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/leads-batch`,
  processImportLeads: (panelId: string, requestId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/leads-batch/${requestId}`,
  uploadManualActivationFile: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/emails/activations`,
  emailBlocklist: () => `${API_BASE_PATH}/email-domain-blocklist`,
  buyerIDBlocklist: () => `${API_BASE_PATH}/blocked-buyers`,
  deleteEmailBlocklist: (id: string) =>
    `${API_BASE_PATH}/email-domain-blocklist/${id}`,
  deleteBuyerIdBlocklist: (id: string) =>
    `${API_BASE_PATH}/blocked-buyers/${id}`,
  getManualPayoutFile: (panelPaymentMethodId?: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts/file`,
  generateManualPayoutFile: (panelPaymentMethodId?: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts`,
  getManualPayoutUploadHistories: (panelPaymentMethodId?: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts/uploads`,
  getManualPayoutErrorFile: (batchId: string) =>
    `${API_BASE_PATH}/panels/pending-payouts/${batchId}/file`,
  createManualPayoutImportTask: (panelPaymentMethodId: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts/uploads`,
  processManualPayout: (panelPaymentMethodId: string, batchId: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts/uploads/${batchId}`,
  getManualPayoutImports: (panelPaymentMethodId?: string) =>
    `${API_BASE_PATH}/panels/panelpaymentmethods/${panelPaymentMethodId}/pending-payouts`,
  getDynamicIncentiveModels: (panelId: string) =>
    `${API_BASE_PATH}/v2/panels/${panelId}/dynamic-incentive-models`,
  getDynamicIncentiveModel: (modelId: string) =>
    `${API_BASE_PATH}/dynamic-incentive-models/${modelId}`,
  createDynamicIncentiveModel: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/dynamic-incentive-models`,
  updateDynamicIncentiveModel: (modelId: string) =>
    `${API_BASE_PATH}/dynamic-incentive-models/${modelId}`,
  deleteDynamicIncentiveModel: (modelId: string) =>
    `${API_BASE_PATH}/dynamic-incentive-models/${modelId}`,
  getDynamicIncentiveModelClusters: () =>
    `${API_BASE_PATH}/dynamic-incentive-models/clusters-config`,
  activateDynamicIncentiveModel: (modelId: string) =>
    `${API_BASE_PATH}/dynamic-incentive-models/${modelId}/activate`,
  deactivateDynamicIncentiveModel: (modelId: string) =>
    `${API_BASE_PATH}/dynamic-incentive-models/${modelId}/deactivate`,
  updateRecommendedSettings: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/recommended-settings`,
  updateRecommendedSettingsCompanyLevel: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/recommended-settings`,
  updateAutomatedTargetingSettings: (panelId: string, modelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/dynamic-incentive-models/${modelId}/automated-targeting`,
  changeAutomatedTargetingStatus: (panelId: string, modelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/dynamic-incentive-models/${modelId}/automated-targeting/status`,
  updatePiiStudyEligibilitySettings: (panelId: string, modelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/dynamic-incentive-models/${modelId}/pii-eligibility`,
  closeAccount: (panelistId: string) =>
    `${API_BASE_PATH}/panelists/${panelistId}/close-account`,
  getOpportunitiesReportData: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/opportunities`,
  getCurrencyExchangeRate: () => `${API_BASE_PATH}/currencies/exchange-rate`,
  sendWave: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/opportunities/waves`,
  getPermissions: () => `${LAS_API_BASE_PATH}/v1/authorization/exchange`,
  previewWave: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/opportunities/waves/preview`,
  liftSurveyQuarantine: (surveyId: string) =>
    `${API_BASE_PATH}/surveys/${surveyId}/quarantine/lift`,
  getTaggedProfilerQuestions: () =>
    `${API_BASE_PATH}/profiler/tagged-questions`,
  updateTaggedProfilerQuestion: (marketplaceQuestionId: string) =>
    `${API_BASE_PATH}/profiler/tagged-questions/${marketplaceQuestionId}`,
  updateCompanySettings: (companyId: string) =>
    `${API_BASE_PATH}/companies/${companyId}/settings`,
  deleteRecruitmentMarketingAccount: (connectionId: string) =>
    `${API_BASE_PATH}/recruitment-marketing/connections/${connectionId}`,

  // Panel-level buyer blocklist (Open Exchange survey sourcing). Replaces the
  // whole list. Distinct from the global `buyerIDBlocklist()` (`/blocked-buyers`).
  replaceBlockedBuyers: (panelId: string) =>
    `${API_BASE_PATH}/panels/${panelId}/blocked-buyers`,
};
